<template>
  <div class="lesson-text-box relative">
    <!-- Показуємо placeholder якщо блок пустий -->
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <!-- Показуємо контент якщо блок не пустий -->
    <div v-else
      class="w-full h-full m-0 box-border transition-all relative overflow-hidden main-group"
      :style="[
        CssStyleHelpers.background(
          fields.mainGroup?.color,
          fields.mainGroup?.isShowColor
        ),
        CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
        CssStyleHelpers.margins(fields.mainGroup?.margins),
        CssStyleHelpers.paddings(fields.mainGroup?.paddings),
        CssStyleHelpers.filterShadow(fields.mainGroup?.shadow),
        {
          '--main-padding-top-mobile': `${fields.mainGroup?.paddingsMobile?.top ?? fields.mainGroup?.paddings?.top ?? 0}px`,
          '--main-padding-right-mobile': `${fields.mainGroup?.paddingsMobile?.right ?? fields.mainGroup?.paddings?.right ?? 0}px`,
          '--main-padding-bottom-mobile': `${fields.mainGroup?.paddingsMobile?.bottom ?? fields.mainGroup?.paddings?.bottom ?? 0}px`,
          '--main-padding-left-mobile': `${fields.mainGroup?.paddingsMobile?.left ?? fields.mainGroup?.paddings?.left ?? 0}px`,
        },
      ]"
    >
      <!-- Медиа контент як фон -->
      <div v-if="fields.mainGroup?.isShowMedia" class="media-background">
        <MediaCard
          fill
          :media-type="fields.mainGroup?.type"
          :image-props="fields.mainGroup?.imageProps"
          :video-props="fields.mainGroup?.videoProps"
          :mask-props="fields.mainGroup?.maskProps"
          :object-fit="fields.mainGroup?.objectFit || 'cover'"
        />
      </div>

      <!-- Паттерн як фон через псевдоелемент -->
      <div 
        v-if="fields.mainGroup?.isShowPattern" 
        class="pattern-background absolute inset-0 pointer-events-none"
        :style="StyleUtils.pattern(fields.mainGroup?.pattern, fields.mainGroup?.isShowPattern)"
      ></div>

      <!-- Заголовок картки -->
      <div v-if="fields.titleGroup?.isShown && fields.titleGroup?.title"
        class="w-full relative z-10"
        :style="[
          CssStyleHelpers.margins({
            bottom: fields.titleGroup?.spacing,
          }),
        ]"
      >
        <TextCard
          class="lesson-title"
          :text="fields.titleGroup?.title"
        />
      </div>

      <!-- Контент з медіа та текстом -->
      <div
        class="content-wrapper flex relative z-10 "
        :style="[
          CssStyleHelpers.flexDirection(fields.mediaGroup?.gridReverse),
          CssStyleHelpers.alignItems(fields.mediaGroup?.align || 'center'),
          CssStyleHelpers.gap(fields.mediaGroup?.gap),
        ]"
      >
        <!-- Заголовок тексту (винесено для можливості зміни порядку на мобільних) -->
        <div 
          v-if="fields.textTitleGroup?.isShown && fields.textTitleGroup?.title && fields.textTitleGroup?.titleAboveMedia"
          class="w-full text-title-wrapper text-title-above-media"
          :style="[
            CssStyleHelpers.margins({
              bottom: fields.textTitleGroup?.spacing,
            }),
          ]"
        >
          <TextCard
            class="lesson-text-title"
            :text="fields.textTitleGroup?.title"
          />
        </div>

        <!-- Медіа секція -->
        <div
          v-if="fields.mediaGroup?.isShown"
          class="media-section flex-shrink-0"
          :style="{
            width: fields.mediaGroup?.gridWidth ? `${fields.mediaGroup.gridWidth}%` : 'auto',
            ...(fields.mediaGroup?.objectFit === 'cover' ? {
              height: '-webkit-fill-available',
              minHeight: '-webkit-fill-available'
            } : {})
          }"
        >
          <div
            class="media-container overflow-hidden"
            :style="[
              CssStyleHelpers.borderRadius(fields.mediaGroup?.mediaBorderRadius),
              CssStyleHelpers.paddings(fields.mediaGroup?.paddings),
              CssStyleHelpers.filterShadow(fields.mediaGroup?.shadow),
              fields.mediaGroup?.objectFit === 'cover' ? {
                height: '-webkit-fill-available',
                minHeight: '-webkit-fill-available'
              } : {}
            ]"
          >
            <MediaCard
              v-if="fields.mediaGroup?.mediaType"
              :media-type="fields.mediaGroup?.mediaType"
              :image-props="fields.mediaGroup?.imageProps"
              :video-props="fields.mediaGroup?.videoProps"
              :mask-props="fields.mediaGroup?.maskProps"
              :object-fit="fields.mediaGroup?.objectFit || 'contain'"
              :aspect-ratio="fields.mediaGroup?.aspectRatio"
              :border-radius="fields.mediaGroup?.borderRadius"
              :mode="mode"
            />
          </div>
        </div>

        <!-- Текстова секція -->
        <div
          v-if="fields.contentGroup?.text"
          class="text-section flex-1"
          :style="[
            CssStyleHelpers.alignItems(fields.contentGroup?.align || 'center'),
          ]"
        >
          <!-- Заголовок тексту -->
          <div 
            v-if="fields.textTitleGroup?.isShown && fields.textTitleGroup?.title"
            class="w-full text-title-inline-desktop"
            :class="{ 'text-title-above-media-mobile': fields.textTitleGroup?.titleAboveMedia }"
            :style="[
              CssStyleHelpers.margins({
                bottom: fields.textTitleGroup?.spacing,
              }),
            ]"
          >
            <TextCard
              class="lesson-text-title"
              :text="fields.textTitleGroup?.title"
            />
          </div>

          <!-- Основний текст -->
          <div
            class="lesson-text-content text-black"
            :style="[
              CssStyleHelpers.blur(
                fields.contentGroup?.blur,
                fields.contentGroup?.isShowOverlay
              ),
              CssStyleHelpers.borderRadius(fields.contentGroup?.borderRadius),
              CssStyleHelpers.paddings(fields.contentGroup?.paddings),
              CssStyleHelpers.background(
                fields.contentGroup?.backgroundColor,
                fields.contentGroup?.isShowOverlay
              ),
              CssStyleHelpers.filterShadow(fields.contentGroup?.shadow),
            ]"
          >
            <TextCard :text="fields.contentGroup?.text" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import { useDataFields } from "../composables/data";
import MediaCard from "../cards/MediaCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import { ILayoutComponentData } from "../types/ILayoutFieldData";
import {
  CssImageHelpers,
  CssStyleHelpers,
  CssVideoHelpers,
  StyleUtils,
} from "../utils/cssStyleHelpers";
import TextCard from "../cards/TextCard.vue";

const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

// Функції для застосування дефолтних значень
const applyDefaultPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData)
    return {
      titleGroup: {},
      textTitleGroup: {},
      contentGroup: {},
      mediaGroup: {},
      mainGroup: {},
    };

  const { getData } = useDataFields(newData);

  return {
    titleGroup: {
      title: getData("titleGroup.title")?.text,
      isShown: getData("titleGroup.isShown")?.value,
      spacing: getData("titleGroup.spacing")?.value,
    },
    textTitleGroup: {
      title: getData("textTitleGroup.title")?.text,
      isShown: getData("textTitleGroup.isShown")?.value,
      spacing: getData("textTitleGroup.spacing")?.value,
      titleAboveMedia: getData("textTitleGroup.titleAboveMedia")?.value,
    },
    contentGroup: {
      text: getData("contentGroup.content")?.text,
      isShowOverlay: getData("contentGroup.isShowOverlay")?.value,
      backgroundColor: getData("contentGroup.backgroundColor")?.value,
      borderRadius: getData("contentGroup.borderRadius")?.value,
      blur: getData("contentGroup.blur")?.value,
      shadow: getData("contentGroup.shadow"),
      align: getData("contentGroup.align")?.value || getData("mediaGroup.align")?.value,
      paddings: applyDefaultPaddings(getData("contentGroup.paddings")),
    },
    mediaGroup: {
      isShown: getData("mediaGroup.isShown")?.value,
      mediaType: getData("mediaGroup.media")?.type,
      objectFit: getData("mediaGroup.media")?.objectFit ?? 'contain',
      imageProps: CssImageHelpers.imageProps(getData("mediaGroup.media")),
      videoProps: CssVideoHelpers.videoProps(getData("mediaGroup.media")),
      maskProps: getData("mediaGroup.mask"),
      aspectRatio: getData("mediaGroup.media")?.aspectRatio,
      borderRadius: getData("mediaGroup.media")?.borderRadius,
      gridWidth: getData("mediaGroup.gridWidth")?.value,
      gridReverse: getData("mediaGroup.gridReverse")?.value,
      gap: getData("mediaGroup.gap")?.value,
      align: getData("mediaGroup.align")?.value,
      shadow: getData("mediaGroup.shadow"),
      mediaBorderRadius: getData("mediaGroup.mediaBorderRadius")?.value,
      paddings: applyDefaultPaddings(getData("mediaGroup.paddings")),
    },
    mainGroup: {
      isShowMedia: getData("mainGroup.isShowMedia")?.value,
      type: getData("mainGroup.media")?.type,
      objectFit: getData("mainGroup.media")?.objectFit ?? 'contain',
      imageProps: CssImageHelpers.imageProps(getData("mainGroup.media")),
      videoProps: CssVideoHelpers.videoProps(getData("mainGroup.media")),
      maskProps: getData("mainGroup.mediaMask"),
      color: getData("mainGroup.color")?.value,
      isShowColor: getData("mainGroup.isShowColor")?.value,
      borderRadius: getData("mainGroup.borderRadius")?.value,
      // Зовнішня тінь усього блоку (Блок → Ефекти → Тінь).
      shadow: getData("mainGroup.shadow"),
      isShowPattern: getData("mainGroup.isShowPattern")?.value,
      pattern: getData("mainGroup.pattern"),
      paddings: applyDefaultPaddings(getData("mainGroup.paddings")),
      paddingsMobile: applyDefaultPaddings(
        getData("mainGroup.paddingsMobile") || getData("mainGroup.paddings")
      ),
      margins: applyDefaultMargins(getData("mainGroup.margins")),
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasTitle = fields.value?.titleGroup?.title && fields.value?.titleGroup?.title?.trim() !== '';
  const hasTextTitle = fields.value?.textTitleGroup?.title && fields.value?.textTitleGroup?.title?.trim() !== '';
  const hasContent = fields.value?.contentGroup?.text && fields.value?.contentGroup?.text?.trim() !== '';
  const hasMedia = fields.value?.mediaGroup?.isShown && (
    fields.value?.mediaGroup?.imageProps?.imageSrc || 
    fields.value?.mediaGroup?.videoProps?.videoSrc
  );
  const hasMainMedia = fields.value?.mainGroup?.isShowMedia && (
    fields.value?.mainGroup?.imageProps?.imageSrc || 
    fields.value?.mainGroup?.videoProps?.videoSrc
  );
  const hasPattern = fields.value?.mainGroup?.isShowPattern;
  const hasColor = fields.value?.mainGroup?.isShowColor && fields.value?.mainGroup?.color;
  
  return !(hasTitle || hasTextTitle || hasContent || hasMedia || hasMainMedia || hasPattern || hasColor);
});
</script>

<style scoped>
@reference "tailwindcss";
.lesson-text-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}

.lesson-title {
  @apply mb-4 relative z-10;
  color: var(--text-primary);
  border-radius: 0.5rem;
}

.lesson-text-title {
  @apply mb-4 relative z-10;
  color: var(--text-primary);
  border-radius: 0.5rem;
}

.lesson-text-content {
  @apply text-base leading-relaxed m-0 relative z-10;
}

.content-wrapper {
  @apply w-full;
}

.text-title-wrapper {
  @apply w-full;
  display: none; /* Приховано на десктопі, показується на мобільних */
}

.text-title-inline-desktop {
  @apply w-full;
}

.media-section {
  @apply flex items-center;
}

.media-container {
  @apply w-full;
}

.media-background {
  @apply w-full h-full absolute inset-0 z-0;
  border-radius: inherit;
  overflow: hidden;
}

.pattern-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: var(--pattern-url);
  background-repeat: repeat;
  background-size: var(--pattern-width) var(--pattern-height);
  opacity: var(--pattern-opacity);
  z-index: 0;
  pointer-events: none;
}

.media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.media-background :deep(.media-container-layer),
.media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}

.media-background :deep(img),
.media-background :deep(video) {
  @apply w-full h-full object-cover;
}



@container (max-width: 640px) {
  .main-group {
    padding-top: var(--main-padding-top-mobile) !important;
    padding-right: var(--main-padding-right-mobile) !important;
    padding-bottom: var(--main-padding-bottom-mobile) !important;
    padding-left: var(--main-padding-left-mobile) !important;
  }

  .content-wrapper{
    flex-direction: column !important;
  }
  .media-section {
    width: auto !important;
  }
  .text-title-wrapper.text-title-above-media {
    order: -1 !important;
    display: block !important;
  }
  .text-title-inline-desktop.text-title-above-media-mobile {
    display: none !important;
  }
}

</style>

<style>
@import "../styles/dialogs-styles.css";
</style>

